import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Pencil, Trash2, Plus, Clock, Wallet, Gauge, Target, Lightbulb, CalendarDays, ArrowUpRight, ArrowDownRight, ChevronDown, ChevronRight } from "lucide-react";
import { SafeChart } from "@/components/SafeChart";

import {
  BarChart, Bar, XAxis, YAxis, Tooltip as RTooltip,
  PieChart, Pie, Cell, LineChart, Line, ReferenceLine, CartesianGrid,
} from "recharts";
import TimeEntryModal from "@/components/time/TimeEntryModal";
import { timeCategoryLabel, fmtDur, fmtEuro, billingTypeLabel, TIME_CATEGORIES } from "@/lib/constants";
import { toast } from "sonner";

const PERIODS = [
  { id: "today", label: "Aujourd'hui" },
  { id: "week", label: "Cette semaine" },
  { id: "month", label: "Ce mois" },
  { id: "last_month", label: "Mois précédent" },
  { id: "quarter", label: "Trimestre" },
  { id: "year", label: "Année" },
  { id: "custom", label: "Période personnalisée" },
];

const isoDay = (d) => d.toISOString().slice(0, 10);
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const sumSec = (list) => list.reduce((a, e) => a + (e.duration_sec || 0), 0);

function rangeFor(period, custom) {
  const now = new Date();
  const today = isoDay(now);
  const monday = addDays(now, -((now.getDay() + 6) % 7));
  switch (period) {
    case "today": return { from: today, to: today };
    case "week": return { from: isoDay(monday), to: isoDay(addDays(monday, 6)) };
    case "month": return { from: `${today.slice(0, 7)}-01`, to: today };
    case "last_month": {
      const d = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      return { from: isoDay(d), to: isoDay(new Date(now.getFullYear(), now.getMonth(), 0)) };
    }
    case "quarter": return { from: isoDay(addDays(now, -90)), to: today };
    case "year": return { from: `${now.getFullYear()}-01-01`, to: today };
    case "custom": return { from: custom.from || today, to: custom.to || today };
    default: return { from: today, to: today };
  }
}

function prevRange({ from, to }) {
  const len = Math.round((new Date(to) - new Date(from)) / 86400000) + 1;
  const prevTo = isoDay(addDays(new Date(`${from}T12:00:00`), -1));
  const prevFrom = isoDay(addDays(new Date(`${prevTo}T12:00:00`), -(len - 1)));
  return { prevFrom, prevTo };
}

const STATUS_META = {
  rentable: { label: "Rentable", cls: "bg-green-50 text-green-700 border-green-200", rank: 3 },
  surveiller: { label: "À surveiller", cls: "bg-orange-50 text-orange-700 border-orange-200", rank: 2 },
  faible: { label: "Peu rentable", cls: "bg-red-50 text-red-700 border-red-200", rank: 1 },
  none: { label: "—", cls: "bg-neutral-50 text-neutral-400 border-neutral-200", rank: 0 },
};

export default function Statistiques() {
  const [entries, setEntries] = useState([]);
  const [clients, setClients] = useState([]);
  const [settings, setSettings] = useState({ target_rate: 60, warn_threshold: 50 });
  const [period, setPeriod] = useState("month");
  const [custom, setCustom] = useState({ from: "", to: "" });
  const [clientFilter, setClientFilter] = useState("");
  const [catFilter, setCatFilter] = useState("");
  const [calView, setCalView] = useState("semaine");
  const [chartView, setChartView] = useState("bar");
  const [sort, setSort] = useState({ key: "ca", dir: -1 });
  const [entryModal, setEntryModal] = useState({ open: false, entry: null });

  const load = () => {
    api.get("/time/entries/list").then((r) => setEntries(r.data)).catch(() => {});
    api.get("/clients").then((r) => setClients(r.data)).catch(() => {});
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  };
  useEffect(() => {
    load();
    const h = () => load();
    window.addEventListener("sf:time", h);
    return () => window.removeEventListener("sf:time", h);
  }, []);

  const now = new Date();
  const today = isoDay(now);
  const monday = isoDay(addDays(now, -((now.getDay() + 6) % 7)));
  const monthStart = `${today.slice(0, 7)}-01`;
  const { from, to } = rangeFor(period, custom);
  const { prevFrom, prevTo } = prevRange({ from, to });

  const filtered = entries.filter((e) =>
    (!clientFilter || e.client_id === clientFilter) && (!catFilter || e.category === catFilter));
  const inRange = filtered.filter((e) => e.date >= from && e.date <= to);
  const [openGroups, setOpenGroups] = useState({});
  const grouped = (() => {
    const byKey = {};
    for (const e of inRange) {
      const k = `${e.date}|${e.client_id}`;
      const g = (byKey[k] = byKey[k] || { key: k, date: e.date, client_id: e.client_id, client_name: e.client_name, sec: 0, entries: [] });
      g.sec += e.duration_sec || 0;
      g.entries.push(e);
    }
    return Object.values(byKey).sort((a, b) => b.date.localeCompare(a.date) || a.client_name.localeCompare(b.client_name));
  })();
  const inPrev = filtered.filter((e) => e.date >= prevFrom && e.date <= prevTo);

  const secToday = sumSec(filtered.filter((e) => e.date === today));
  const secWeek = sumSec(filtered.filter((e) => e.date >= monday && e.date <= today));
  const secMonth = sumSec(filtered.filter((e) => e.date >= monthStart && e.date <= today));
  const rangeSec = sumSec(inRange);
  const prevSec = sumSec(inPrev);
  const rangeDays = Math.max(1, Math.round((new Date(to) - new Date(from)) / 86400000) + 1);
  const avgDay = rangeSec / rangeDays;
  const delta = prevSec > 0 ? Math.round(((rangeSec - prevSec) / prevSec) * 100) : null;

  const byClient = {};
  inRange.forEach((e) => {
    (byClient[e.client_id] = byClient[e.client_id] || { sec: 0, name: e.client_name }).sec += e.duration_sec;
  });
  const clientChart = Object.entries(byClient)
    .map(([id, v]) => ({ name: v.name, heures: +(v.sec / 3600).toFixed(2), color: clients.find((c) => c.id === id)?.color || "#A3A3A3" }))
    .sort((a, b) => b.heures - a.heures);

  const byDay = {};
  inRange.forEach((e) => { byDay[e.date] = (byDay[e.date] || 0) + e.duration_sec; });
  const evoTime = Object.entries(byDay).sort().map(([d, s]) => ({
    date: `${d.slice(8, 10)}/${d.slice(5, 7)}`, heures: +(s / 3600).toFixed(2),
  }));

  const nMonths = (f, t) => {
    const [y1, m1] = f.split("-").map(Number);
    const [y2, m2] = t.split("-").map(Number);
    return (y2 - y1) * 12 + (m2 - m1) + 1;
  };

  const computeRows = (f, t) => clients.map((c) => {
    const type = c.billing_type || "";
    const amount = Number(c.billing_amount) || 0;
    const secRange = sumSec(inRange.filter((e) => e.client_id === c.id && e.date >= f && e.date <= t));
    const secAll = sumSec(entries.filter((e) => e.client_id === c.id));
    let hours; let ca;
    if (type === "projet") { hours = secAll / 3600; ca = amount; }
    else if (type === "horaire") { hours = secRange / 3600; ca = hours * amount; }
    else if (type === "forfait") { hours = secRange / 3600; ca = amount * nMonths(f, t); }
    else { hours = secRange / 3600; ca = 0; }
    const rate = hours > 0 && ca > 0 ? ca / hours : null;
    const status = rate == null ? "none" : rate >= settings.target_rate ? "rentable" : rate >= settings.warn_threshold ? "surveiller" : "faible";
    let budgetH = null; let consumedH = null; let budgetPct = null;
    if (amount > 0 && (type === "forfait" || type === "projet") && settings.target_rate > 0) {
      budgetH = amount / settings.target_rate;
      consumedH = type === "projet" ? secAll / 3600
        : sumSec(entries.filter((e) => e.client_id === c.id && e.date >= monthStart && e.date <= today)) / 3600;
      budgetPct = Math.round((consumedH / budgetH) * 100);
    }
    return { id: c.id, name: c.name, color: c.color, type, amount, hours, ca, rate, status, budgetH, consumedH, budgetPct };
  }).filter((r) => r.amount > 0 || r.hours > 0);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const rows = useMemo(() => computeRows(from, to), [entries, clients, settings, from, to]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const monthRows = useMemo(() => computeRows(monthStart, today), [entries, clients, settings, monthStart, today]);

  const sortedRows = [...rows].sort((a, b) => {
    const k = sort.key;
    let va; let vb;
    if (k === "client") { va = a.name; vb = b.name; return sort.dir * va.localeCompare(vb); }
    if (k === "type") { va = billingTypeLabel(a.type); vb = billingTypeLabel(b.type); return sort.dir * va.localeCompare(vb); }
    if (k === "status") { va = STATUS_META[a.status].rank; vb = STATUS_META[b.status].rank; }
    else { va = a[k] ?? -1; vb = b[k] ?? -1; }
    return sort.dir * (va - vb);
  });

  const monthCA = monthRows.reduce((a, r) => a + (r.type === "projet" ? 0 : r.ca), 0);
  const monthHours = monthRows.reduce((a, r) => a + r.hours, 0);
  const globalRate = monthHours > 0 && monthCA > 0 ? monthCA / monthHours : null;
  const rated = monthRows.filter((r) => r.status !== "none");
  const rentables = monthRows.filter((r) => r.status === "rentable").length;
  const sousSeuil = monthRows.filter((r) => r.status === "faible").length;

  const evoRate = [];
  for (let i = 5; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const mFrom = isoDay(d);
    const mToReal = isoDay(new Date(d.getFullYear(), d.getMonth() + 1, 0));
    const mTo = mToReal < today ? mToReal : today;
    const mEntries = entries.filter((e) => e.date >= mFrom && e.date <= mTo);
    const h = sumSec(mEntries) / 3600;
    let ca = 0;
    clients.forEach((c) => {
      const amount = Number(c.billing_amount) || 0;
      if (c.billing_type === "forfait") ca += amount;
      if (c.billing_type === "horaire") ca += (sumSec(mEntries.filter((e) => e.client_id === c.id)) / 3600) * amount;
    });
    evoRate.push({ mois: d.toLocaleDateString("fr-FR", { month: "short" }), taux: h > 0 && ca > 0 ? +(ca / h).toFixed(2) : null });
  }

  const catAgg = {};
  rows.forEach((r) => {
    if (r.ca <= 0 || r.hours <= 0) return;
    inRange.filter((e) => e.client_id === r.id).forEach((e) => {
      const share = ((e.duration_sec / 3600) / r.hours) * r.ca;
      (catAgg[e.category] = catAgg[e.category] || { ca: 0, sec: 0 });
      catAgg[e.category].ca += share;
      catAgg[e.category].sec += e.duration_sec;
    });
  });
  const catRates = Object.entries(catAgg)
    .map(([cat, v]) => ({ cat, rate: v.sec > 0 ? v.ca / (v.sec / 3600) : 0, sec: v.sec }))
    .sort((a, b) => b.rate - a.rate);

  const ratedRows = rows.filter((r) => r.rate != null);
  const bestRow = ratedRows.length ? [...ratedRows].sort((a, b) => b.rate - a.rate)[0] : null;
  const worstRow = ratedRows.length ? [...ratedRows].sort((a, b) => a.rate - b.rate)[0] : null;
  const rangeCA = rows.reduce((a, r) => a + (r.type === "projet" ? 0 : r.ca), 0);
  const rangeHours = rows.reduce((a, r) => a + r.hours, 0);
  const rangeRate = rangeHours > 0 && rangeCA > 0 ? rangeCA / rangeHours : null;

  const delEntry = async (e) => {
    await api.delete(`/time/entries/${e.id}`);
    toast.success("Entrée supprimée");
    load();
  };

  const sel = "text-sm border border-neutral-200 rounded-md px-2.5 py-1.5 bg-white";
  const weekDays = Array.from({ length: 7 }, (_, i) => isoDay(addDays(new Date(`${monday}T12:00:00`), i)));
  const JOURS = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];
  const dayEntries = (d) => filtered.filter((e) => e.date === d);
  const weekTotal = weekDays.reduce((a, d) => a + sumSec(dayEntries(d)), 0);

  const monthCells = useMemo(() => {
    const first = new Date(now.getFullYear(), now.getMonth(), 1);
    const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
    const offset = (first.getDay() + 6) % 7;
    const cells = Array(offset).fill(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(isoDay(new Date(now.getFullYear(), now.getMonth(), d)));
    return cells;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="space-y-6" data-testid="stats-page">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-0">
          <h1 className="font-heading text-3xl font-bold">Rentabilité</h1>
          <p className="text-neutral-500 text-sm mt-1">Temps de travail, taux horaire réel et rentabilité par client.</p>
        </div>
        <Button data-testid="add-time-btn" onClick={() => setEntryModal({ open: true, entry: null })}
          className="bg-neutral-900 hover:bg-neutral-800 text-white gap-1.5">
          <Plus className="w-4 h-4" /> Ajouter du temps
        </Button>
      </div>

      <div className="flex flex-wrap gap-2 items-center" data-testid="stats-filters">
        <select className={sel} data-testid="stats-period" value={period} onChange={(e) => setPeriod(e.target.value)}>
          {PERIODS.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
        </select>
        {period === "custom" && (
          <>
            <Input type="date" className="w-auto" data-testid="stats-custom-from" value={custom.from} onChange={(e) => setCustom((c) => ({ ...c, from: e.target.value }))} />
            <Input type="date" className="w-auto" data-testid="stats-custom-to" value={custom.to} onChange={(e) => setCustom((c) => ({ ...c, to: e.target.value }))} />
          </>
        )}
        <select className={sel} data-testid="stats-client-filter" value={clientFilter} onChange={(e) => setClientFilter(e.target.value)}>
          <option value="">Tous les clients</option>
          {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select className={sel} data-testid="stats-category-filter" value={catFilter} onChange={(e) => setCatFilter(e.target.value)}>
          <option value="">Toutes prestations</option>
          {TIME_CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
        </select>
      </div>

      {/* Temps de travail */}
      <div>
        <h2 className="font-heading font-bold text-lg mb-3 flex items-center gap-2"><Clock className="w-4 h-4 text-neutral-400" /> Temps de travail</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { l: "Temps aujourd'hui", v: fmtDur(secToday), tid: "kpi-today" },
            { l: "Cette semaine", v: fmtDur(secWeek), tid: "kpi-week" },
            { l: "Ce mois", v: fmtDur(secMonth), tid: "kpi-month" },
            { l: "Moyenne / jour (période)", v: fmtDur(avgDay), tid: "kpi-avg" },
          ].map((k) => (
            <div key={k.tid} className="bg-white border border-neutral-200 rounded-xl shadow-sm p-5" data-testid={k.tid}>
              <p className="font-heading text-2xl font-bold">{k.v}</p>
              <p className="text-xs text-neutral-500 mt-1">{k.l}</p>
            </div>
          ))}
        </div>
        {delta != null && (
          <p className={`text-sm mt-3 flex items-center gap-1 ${delta >= 0 ? "text-green-600" : "text-red-600"}`} data-testid="stats-comparison">
            {delta >= 0 ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
            {fmtDur(rangeSec)} sur la période — {delta >= 0 ? "+" : ""}{delta} % par rapport à la période précédente
          </p>
        )}
      </div>

      {/* Calendrier du temps */}
      <div className="bg-white border border-neutral-200 rounded-xl shadow-sm overflow-hidden" data-testid="time-calendar">
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-neutral-100">
          <h3 className="font-heading font-bold flex items-center gap-2"><CalendarDays className="w-4 h-4 text-neutral-400" /> Calendrier du temps passé</h3>
          <div className="flex gap-1">
            {[["jour", "Jour"], ["semaine", "Semaine"], ["mois", "Mois"]].map(([id, l]) => (
              <button key={id} data-testid={`cal-view-${id}`} onClick={() => setCalView(id)}
                className={`text-xs px-2.5 py-1.5 rounded-md transition-colors ${calView === id ? "bg-neutral-900 text-white" : "text-neutral-500 hover:bg-neutral-100"}`}>
                {l}
              </button>
            ))}
          </div>
        </div>

        {calView === "jour" && (
          <div className="p-5 space-y-2" data-testid="cal-day">
            <p className="text-sm text-neutral-500 mb-1">Aujourd'hui — {today.split("-").reverse().join("/")}</p>
            {dayEntries(today).length === 0 && <p className="text-sm text-neutral-400 py-4 text-center">Aucun temps enregistré aujourd'hui.</p>}
            {dayEntries(today).sort((a, b) => a.start.localeCompare(b.start)).map((e) => (
              <div key={e.id} className="flex items-center gap-3 border border-neutral-200 rounded-lg px-4 py-3">
                <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: clients.find((c) => c.id === e.client_id)?.color || "#999" }} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium">{e.client_name}</p>
                  <p className="text-xs text-neutral-400">{(e.start || "").slice(11, 16)} – {(e.end || "").slice(11, 16)} · {timeCategoryLabel(e.category)}{e.note ? ` · ${e.note}` : ""}</p>
                </div>
                <span className="text-sm font-semibold">{fmtDur(e.duration_sec)}</span>
              </div>
            ))}
            <p className="text-sm font-semibold pt-2" data-testid="cal-day-total">Total : {fmtDur(sumSec(dayEntries(today)))}</p>
          </div>
        )}

        {calView === "semaine" && (
          <div className="p-4 overflow-x-auto" data-testid="cal-week">
            <div className="grid grid-cols-7 gap-2 min-w-[900px]">
              {weekDays.map((d, i) => {
                const list = dayEntries(d).sort((a, b) => a.start.localeCompare(b.start));
                return (
                  <div key={d} className={`rounded-lg border ${d === today ? "border-neutral-900" : "border-neutral-200"} bg-neutral-50/50 flex flex-col`} data-testid={`cal-day-col-${d}`}>
                    <p className="text-xs font-semibold uppercase tracking-wide text-neutral-500 px-2.5 pt-2.5">{JOURS[i]} {d.slice(8, 10)}</p>
                    <div className="flex-1 px-2 py-2 space-y-1.5">
                      {list.map((e) => (
                        <div key={e.id} className="bg-white border border-neutral-200 rounded-md p-2 border-l-4" style={{ borderLeftColor: clients.find((c) => c.id === e.client_id)?.color || "#999" }}>
                          <p className="text-[10px] text-neutral-400 font-mono">{(e.start || "").slice(11, 16)} – {(e.end || "").slice(11, 16)}</p>
                          <p className="text-xs font-medium truncate">{e.client_name}</p>
                          <p className="text-[10px] text-neutral-500">{fmtDur(e.duration_sec)} · {timeCategoryLabel(e.category)}</p>
                        </div>
                      ))}
                    </div>
                    <p className="text-xs font-semibold px-2.5 py-2 border-t border-neutral-200">Total : {fmtDur(sumSec(list))}</p>
                  </div>
                );
              })}
            </div>
            <p className="text-sm font-semibold px-1 pt-3" data-testid="cal-week-total">Total semaine : {fmtDur(weekTotal)}</p>
          </div>
        )}

        {calView === "mois" && (
          <div className="p-4" data-testid="cal-month">
            <div className="grid grid-cols-7 gap-1.5">
              {JOURS.map((j) => <p key={j} className="text-[10px] font-semibold uppercase text-neutral-400 text-center pb-1">{j}</p>)}
              {monthCells.map((d, i) => (
                <div key={i} className={`min-h-[64px] rounded-md border p-1.5 ${d ? "border-neutral-200 bg-white" : "border-transparent"} ${d === today ? "ring-1 ring-neutral-900" : ""}`} data-testid={d ? `cal-month-cell-${d}` : undefined}>
                  {d && (
                    <>
                      <p className="text-[10px] text-neutral-400">{Number(d.slice(8, 10))}</p>
                      {sumSec(dayEntries(d)) > 0 && (
                        <p className="text-xs font-semibold mt-1 bg-blue-50 text-blue-700 rounded px-1 py-0.5 inline-block">{fmtDur(sumSec(dayEntries(d)))}</p>
                      )}
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Temps par client */}
      <div className="bg-white border border-neutral-200 rounded-xl shadow-sm overflow-hidden" data-testid="client-time-chart">
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-neutral-100">
          <h3 className="font-heading font-bold">Temps passé par client</h3>
          <div className="flex gap-1">
            {[["bar", "Barres"], ["pie", "Circulaire"], ["line", "Évolution"]].map(([id, l]) => (
              <button key={id} data-testid={`chart-view-${id}`} onClick={() => setChartView(id)}
                className={`text-xs px-2.5 py-1.5 rounded-md transition-colors ${chartView === id ? "bg-neutral-900 text-white" : "text-neutral-500 hover:bg-neutral-100"}`}>
                {l}
              </button>
            ))}
          </div>
        </div>
        <div className="p-5 h-72">
          {clientChart.length === 0 ? (
            <p className="text-sm text-neutral-400 text-center py-16">Aucun temps enregistré sur la période.</p>
          ) : chartView === "bar" ? (
            <SafeChart width="100%" height="100%">
              <BarChart data={clientChart} layout="vertical" margin={{ left: 20, right: 20 }}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f0f0f0" />
                <XAxis type="number" tickFormatter={(v) => `${v} h`} tick={{ fontSize: 11 }} />
                <YAxis type="category" dataKey="name" width={110} tick={{ fontSize: 11 }} />
                <RTooltip formatter={(v) => [`${v} h`, "Temps"]} />
                <Bar dataKey="heures" radius={[0, 4, 4, 0]}>
                  {clientChart.map((c) => <Cell key={c.name} fill={c.color} />)}
                </Bar>
              </BarChart>
            </SafeChart>
          ) : chartView === "pie" ? (
            <SafeChart width="100%" height="100%">
              <PieChart>
                <Pie data={clientChart} dataKey="heures" nameKey="name" innerRadius="45%" outerRadius="80%" paddingAngle={2}
                  label={({ name, heures }) => `${name} — ${heures} h`} fontSize={11}>
                  {clientChart.map((c) => <Cell key={c.name} fill={c.color} />)}
                </Pie>
                <RTooltip formatter={(v) => [`${v} h`, "Temps"]} />
              </PieChart>
            </SafeChart>
          ) : (
            <SafeChart width="100%" height="100%">
              <LineChart data={evoTime} margin={{ left: 0, right: 20 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                <YAxis tickFormatter={(v) => `${v} h`} tick={{ fontSize: 11 }} />
                <RTooltip formatter={(v) => [`${v} h`, "Temps"]} />
                <Line type="monotone" dataKey="heures" stroke="#395963" strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            </SafeChart>
          )}
        </div>
      </div>

      {/* Rentabilité */}
      <div>
        <h2 className="font-heading font-bold text-lg mb-3 flex items-center gap-2"><Wallet className="w-4 h-4 text-neutral-400" /> Rentabilité — ce mois</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {[
            { l: "Chiffre d'affaires du mois", v: monthCA > 0 ? fmtEuro(monthCA) : "—", tid: "kpi-ca" },
            { l: "Heures travaillées", v: fmtDur(secMonth), tid: "kpi-hours" },
            { l: "Taux horaire réel global", v: globalRate != null ? `${fmtEuro(globalRate)}/h` : "—", tid: "kpi-real-rate" },
            { l: "Taux horaire cible", v: `${fmtEuro(settings.target_rate)}/h`, tid: "kpi-target-rate" },
            { l: "Clients rentables", v: `${rentables} / ${rated.length}`, tid: "kpi-rentables" },
            { l: "Clients sous le seuil", v: `${sousSeuil} / ${rated.length}`, tid: "kpi-sous-seuil" },
          ].map((k) => (
            <div key={k.tid} className="bg-white border border-neutral-200 rounded-xl shadow-sm p-4" data-testid={k.tid}>
              <p className="font-heading text-xl font-bold">{k.v}</p>
              <p className="text-xs text-neutral-500 mt-1">{k.l}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Tableau rentabilité */}
      <div className="bg-white border border-neutral-200 rounded-xl shadow-sm overflow-x-auto" data-testid="rentability-table">
        <table className="w-full text-sm min-w-[900px]">
          <thead>
            <tr className="border-b border-neutral-200 text-left text-xs text-neutral-400 uppercase tracking-wider">
              {[
                ["client", "Client"], ["type", "Type"], ["ca", "CA"], ["hours", "Temps"],
                ["rate", "Taux réel"], ["budget", "Budget temps"], ["status", "Rentabilité"],
              ].map(([k, l]) => (
                <th key={k} className="px-5 py-3 font-medium">
                  <button data-testid={`sort-${k}`} onClick={() => setSort((s) => ({ key: k, dir: s.key === k ? -s.dir : 1 }))}
                    className="hover:text-neutral-900 transition-colors uppercase">
                    {l} {sort.key === k ? (sort.dir === 1 ? "↑" : "↓") : ""}
                  </button>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedRows.map((r) => (
              <tr key={r.id} className="border-b border-neutral-100 last:border-0" data-testid={`rent-row-${r.id}`}>
                <td className="px-5 py-3">
                  <span className="flex items-center gap-2 font-medium">
                    <span className="w-2.5 h-2.5 rounded-full" style={{ background: r.color }} />
                    {r.name}
                  </span>
                </td>
                <td className="px-5 py-3 text-neutral-500">{billingTypeLabel(r.type)}</td>
                <td className="px-5 py-3">{r.ca > 0 ? fmtEuro(r.ca) : "—"}{r.type === "forfait" ? " / mois" : r.type === "projet" ? " / projet" : r.type === "horaire" ? " / h" : ""}</td>
                <td className="px-5 py-3">{fmtDur(r.hours * 3600)}</td>
                <td className="px-5 py-3 font-medium" data-testid={`rate-${r.id}`}>{r.rate != null ? `${fmtEuro(r.rate)}/h` : "—"}</td>
                <td className="px-5 py-3" data-testid={`budget-${r.id}`}>
                  {r.budgetH != null ? (
                    <div className="w-40">
                      <p className={`text-xs mb-1 ${r.budgetPct >= 100 ? "text-red-600 font-semibold" : r.budgetPct >= 80 ? "text-orange-600 font-medium" : "text-neutral-500"}`}>
                        {fmtDur(r.consumedH * 3600)} / {fmtDur(r.budgetH * 3600)} — {r.budgetPct} %
                        {r.budgetPct > 100 && " · dépassé"}
                        {r.budgetPct >= 80 && r.budgetPct <= 100 && " · alerte 80 %"}
                      </p>
                      <div className="h-1.5 rounded-full bg-neutral-100 overflow-hidden">
                        <div className={`h-full rounded-full ${r.budgetPct > 100 ? "bg-red-500" : r.budgetPct >= 80 ? "bg-orange-400" : "bg-blue-500"}`}
                          style={{ width: `${Math.min(100, r.budgetPct)}%` }} />
                      </div>
                    </div>
                  ) : <span className="text-neutral-300">—</span>}
                </td>
                <td className="px-5 py-3">
                  <span className={`text-xs font-medium border rounded-full px-2.5 py-1 ${STATUS_META[r.status].cls}`} data-testid={`status-${r.id}`}>
                    {STATUS_META[r.status].label}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {sortedRows.length === 0 && (
          <p className="px-5 py-10 text-center text-sm text-neutral-400" data-testid="rent-empty">
            Renseignez les données financières de vos clients (onglet Paramètres de l'espace client) et enregistrez du temps pour voir la rentabilité.
          </p>
        )}
      </div>

      {/* Évolution rentabilité */}
      <div className="bg-white border border-neutral-200 rounded-xl shadow-sm overflow-hidden" data-testid="rate-evolution">
        <div className="px-5 py-3.5 border-b border-neutral-100">
          <h3 className="font-heading font-bold flex items-center gap-2"><Gauge className="w-4 h-4 text-neutral-400" /> Évolution du taux horaire réel</h3>
        </div>
        <div className="p-5 h-64">
          <SafeChart width="100%" height="100%">
            <LineChart data={evoRate} margin={{ left: 0, right: 20 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
              <XAxis dataKey="mois" tick={{ fontSize: 11 }} />
              <YAxis tickFormatter={(v) => `${v} €`} tick={{ fontSize: 11 }} />
              <RTooltip formatter={(v) => (v == null ? ["—", "Taux réel"] : [`${v} €/h`, "Taux réel"])} />
              <ReferenceLine y={settings.target_rate} stroke="#16a34a" strokeDasharray="6 4"
                label={{ value: `Objectif : ${settings.target_rate} €/h`, position: "insideTopRight", fontSize: 11, fill: "#16a34a" }} />
              <Line type="monotone" dataKey="taux" stroke="#171717" strokeWidth={2} dot={{ r: 4 }} connectNulls />
            </LineChart>
          </SafeChart>
        </div>
      </div>

      {/* Analyse automatique */}
      <div className="bg-white border border-neutral-200 rounded-xl shadow-sm p-5" data-testid="insights">
        <h3 className="font-heading font-bold flex items-center gap-2 mb-4"><Lightbulb className="w-4 h-4 text-neutral-400" /> Analyse de mon activité</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { l: "Client le plus chronophage", v: clientChart[0] ? `${clientChart[0].name} — ${clientChart[0].heures} h` : "—", tid: "insight-top-client" },
            { l: "Meilleur taux horaire", v: bestRow ? `${bestRow.name} — ${fmtEuro(bestRow.rate)}/h` : "—", tid: "insight-best-rate" },
            { l: "Client le moins rentable", v: worstRow ? `${worstRow.name} — ${fmtEuro(worstRow.rate)}/h` : "—", tid: "insight-worst-rate" },
            { l: "Prestation la plus rentable", v: catRates[0] ? `${timeCategoryLabel(catRates[0].cat)} — ${fmtEuro(catRates[0].rate)}/h` : "—", tid: "insight-best-cat" },
            { l: "Prestation la moins rentable", v: catRates.length > 1 ? `${timeCategoryLabel(catRates[catRates.length - 1].cat)} — ${fmtEuro(catRates[catRates.length - 1].rate)}/h` : "—", tid: "insight-worst-cat" },
            { l: "Temps total travaillé (période)", v: fmtDur(rangeSec), tid: "insight-total-time" },
            { l: "CA généré (période)", v: rangeCA > 0 ? fmtEuro(rangeCA) : "—", tid: "insight-ca" },
            { l: "Taux horaire réel moyen (période)", v: rangeRate != null ? `${fmtEuro(rangeRate)}/h` : "—", tid: "insight-avg-rate" },
          ].map((i) => (
            <div key={i.tid} className="border border-neutral-200 rounded-lg p-4" data-testid={i.tid}>
              <p className="text-xs text-neutral-400">{i.l}</p>
              <p className="text-sm font-semibold mt-1 truncate">{i.v}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Entrées de temps */}
      <div className="bg-white border border-neutral-200 rounded-xl shadow-sm overflow-hidden" data-testid="time-entries">
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-neutral-100">
          <h3 className="font-heading font-bold">Entrées de temps (période)</h3>
          <button data-testid="entries-add-btn" onClick={() => setEntryModal({ open: true, entry: null })}
            className="flex items-center gap-1 text-xs font-medium bg-neutral-900 hover:bg-neutral-800 text-white rounded-md px-2.5 py-1.5 transition-colors">
            <Plus className="w-3.5 h-3.5" /> Ajouter du temps
          </button>
        </div>
        {inRange.length === 0 && <p className="px-5 py-8 text-center text-sm text-neutral-400">Aucune entrée sur la période.</p>}
        {grouped.map((g) => {
          const open = !!openGroups[g.key];
          const single = g.entries.length === 1;
          return (
            <div key={g.key} className="border-b border-neutral-100 last:border-0">
              <div className={`w-full flex items-center gap-3 px-5 py-3 text-left ${single ? "" : "cursor-pointer hover:bg-neutral-50 transition-colors"}`}
                role={single ? undefined : "button"}
                data-testid={`entry-group-${g.key}`}
                onClick={() => !single && setOpenGroups((o) => ({ ...o, [g.key]: !open }))}>
                <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: clients.find((c) => c.id === g.client_id)?.color || "#999" }} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{g.client_name}</p>
                  <p className="text-xs text-neutral-400">{g.date.split("-").reverse().join("/")} · {g.entries.length} entrée{g.entries.length > 1 ? "s" : ""}</p>
                </div>
                <span className="text-sm font-semibold">{fmtDur(g.sec)}</span>
                {single ? (
                  <>
                    <button data-testid={`entry-edit-${g.entries[0].id}`} onClick={() => setEntryModal({ open: true, entry: g.entries[0] })}
                      className="p-1.5 rounded-md text-neutral-400 hover:text-neutral-900 hover:bg-neutral-100 transition-colors"><Pencil className="w-3.5 h-3.5" /></button>
                    <button data-testid={`entry-delete-${g.entries[0].id}`} onClick={() => delEntry(g.entries[0])}
                      className="p-1.5 rounded-md text-neutral-400 hover:text-red-600 hover:bg-red-50 transition-colors"><Trash2 className="w-3.5 h-3.5" /></button>
                  </>
                ) : (open ? <ChevronDown className="w-4 h-4 text-neutral-400 shrink-0" /> : <ChevronRight className="w-4 h-4 text-neutral-400 shrink-0" />)}
              </div>
              {open && !single && (
                <div className="pb-2">
                  {g.entries.map((e) => (
                    <div key={e.id} className="flex items-center gap-3 px-5 py-2 pl-10" data-testid={`entry-${e.id}`}>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-neutral-500 truncate">{timeCategoryLabel(e.category)} · {(e.start || "").slice(11, 16)} → {(e.end || "").slice(11, 16)}{e.note ? ` · ${e.note}` : ""}</p>
                      </div>
                      <span className="text-xs font-semibold">{fmtDur(e.duration_sec)}</span>
                      <button data-testid={`entry-edit-${e.id}`} onClick={() => setEntryModal({ open: true, entry: e })}
                        className="p-1.5 rounded-md text-neutral-400 hover:text-neutral-900 hover:bg-neutral-100 transition-colors"><Pencil className="w-3.5 h-3.5" /></button>
                      <button data-testid={`entry-delete-${e.id}`} onClick={() => delEntry(e)}
                        className="p-1.5 rounded-md text-neutral-400 hover:text-red-600 hover:bg-red-50 transition-colors"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <TimeEntryModal open={entryModal.open} setOpen={(v) => setEntryModal((m) => ({ ...m, open: v }))}
        clients={clients} entry={entryModal.entry} onSaved={load} />
    </div>
  );
}
